import { DocumentDtoStatusEnum, Role } from "../api";
import { useDocuments } from "../modules/settings/hooks/useDocuments";
import { useMe, useMePartner } from "../swr";
import { hasAllDocumentsUploaded, REQUIRED_DOCUMENTS } from "../utils/onboarding";

export type ApprovalStatus = "unapproved" | "pending" | "approved";

export interface ApprovalStatusResult {
  status: ApprovalStatus;
  isLoading: boolean;
  /** True for admins and sellers — they bypass document checks entirely. */
  isExempt: boolean;
}

/**
 * Single source of truth for whether the signed-in user has full access.
 *
 * - admin or seller → "approved" (no document gating)
 * - buyer with required documents not all uploaded → "unapproved"
 * - buyer with documents uploaded but not all approved → "pending"
 * - buyer with all documents approved → "approved"
 *
 * Used by Phase-2 price-gating and Phase-3 chat limits to decide how much of
 * the app to expose.
 */
export function useApprovalStatus(): ApprovalStatusResult {
  const { user, isLoading: isLoadingUser } = useMe();
  const { isBuyer, isSeller, isLoading: isLoadingPartner } = useMePartner();
  const { data: documentsData, isLoading: isLoadingDocuments } = useDocuments({
    page: 1,
    pageSize: 100,
  });

  const isLoading = isLoadingUser || isLoadingPartner || isLoadingDocuments;

  const isAdmin = user?.role === Role.Admin;
  const isExempt = isAdmin || isSeller;

  if (isExempt || !isBuyer) {
    return { status: "approved", isLoading, isExempt };
  }

  if (!hasAllDocumentsUploaded(user)) {
    return { status: "unapproved", isLoading, isExempt };
  }

  const documents = documentsData?.data ?? [];
  const approvedTypes = new Set(
    documents
      .filter((doc) => doc.status === DocumentDtoStatusEnum.Approved)
      .map((doc) => doc.type),
  );
  const allApproved = REQUIRED_DOCUMENTS.every((t) => approvedTypes.has(t));

  return {
    status: allApproved ? "approved" : "pending",
    isLoading,
    isExempt,
  };
}
